import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import FroalaEditor from "react-froala-wysiwyg";
import "froala-editor/css/froala_style.min.css";
import "froala-editor/css/froala_editor.pkgd.min.css";
import "froala-editor/js/plugins.pkgd.min.js";
import FroalaEditorCore from "froala-editor";
import { toast } from "react-toastify";
import "./index.scss";
import { transformEditorText } from "../../../../utils/helpers";
import ContentEditable from "react-contenteditable";
import {
  applyHeadingProps,
  applyParagraphProps,
  getCharacterCount,
  removeInlineHeadingColors,
  removeInlineParagraphColors,
} from "../FroalaHelpers";
import { getSelectedSegmentCharCount } from "../../../../redux/selectors/segmentBuilderSelectors";
import { charLimitInASegment } from "../../../SegmentSettings";
import EmojiPicker from "emoji-picker-react";
import { Box } from "@mui/material";
import { Close } from "@material-ui/icons";

const FroalaTextEditor = ({
  name,
  value: externalValue,
  id,
  onBlur,
  onChange,
  disabled,
  handleEditorCustomization = () => {},
  editorRef,
  editorHeight,
  placeholder = "Hello there!",
  customDefaultFontSize,
  trimUrls = false,
  toolbarButtons = [
    "textColorCustom",
    "paragraphFormat",
    "customBackgroundColor",
    "bold",
    "italic",
    "underline",
    "alignLeft",
    "alignCenter",
    "alignRight",
    "|",
    "simpleUL", // Regular bullet button
    "bulletStyles", // Bullet styles dropdown
    "formatOL",
    "indent",
    "outdent",

    // "formatUL",

    "insertLink",
    "emoticons",
    "specialCharacters",
    "undo",
    "redo",
    "aiAssistant",
  ],
  label = null,
  dynamicLabelName = null,
  type = "description",
  characters = null,
  singleElement = false,
  focused = false,
  lineHeight = null,
  headingProps = null,
  paragraphProps = null,
  isExpandable = false,
  disableAtCharLimit = false,
  popupPositionMode = "default", // "default" | "relative"
  dataEditorId = null,
  autofocus = true,
}) => {
  const [value, setValue] = useState(externalValue || "");
  const [isInteracting, setIsInteracting] = useState(false);
  const [dynamicLabel, setDynamicLabel] = useState(dynamicLabelName);
  const [forceUpdateCounter, forceUpdate] = useState(0);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  // Refs for emoji picker positioning and outside click detection
  const emojiPickerRef = useRef(null);
  const emojiButtonRef = useRef(null);

  // Ref to store the saved selection before opening emoji picker
  const savedSelectionRef = useRef(null);

  // Ref to store which editor opened the emoji picker
  const activeEditorRef = useRef(null);

  // Get the character count from Redux state
  const selectedSegmentCharCount = useSelector(getSelectedSegmentCharCount);
  const currentEditorContribution = getCharacterCount(value); // Get this editor's current character count
  const isAtCharLimit =
    disableAtCharLimit && selectedSegmentCharCount >= charLimitInASegment;

  // Use ref to store the current limit state for event handlers
  const isAtCharLimitRef = useRef(isAtCharLimit);

  // Use ref to store the current segment character count for event handlers
  const selectedSegmentCharCountRef = useRef(selectedSegmentCharCount);

  // Update the ref whenever isAtCharLimit changes
  useEffect(() => {
    isAtCharLimitRef.current = isAtCharLimit;
  }, [isAtCharLimit]);

  // Update the ref whenever selectedSegmentCharCount changes
  useEffect(() => {
    selectedSegmentCharCountRef.current = selectedSegmentCharCount;
  }, [selectedSegmentCharCount]);

  // Update character count ref whenever content changes
  const updateCharCountRef = (editor) => {
    if (!editor || !disableAtCharLimit) return;

    const currentContent = editor.html.get();
    const currentCharCount = getCharacterCount(currentContent);

    // Calculate the total segment count
    const otherEditorsCount =
      selectedSegmentCharCountRef.current - currentEditorContribution;
    const newTotal = otherEditorsCount + currentCharCount;

    selectedSegmentCharCountRef.current = newTotal;
  };

  console.log(
    "isAtCharLimit:",
    isAtCharLimit,
    "totalSegmentChars:",
    selectedSegmentCharCount,
    "thisEditorChars:",
    currentEditorContribution,
    "limit:",
    charLimitInASegment,
    "isAtCharLimitRef:",
    isAtCharLimitRef.current,
    "selectedSegmentCharCountRef:",
    selectedSegmentCharCountRef.current,

    "hama"
  );

  useEffect(() => {
    if (externalValue !== undefined && externalValue !== null) {
      setValue(externalValue);
    } else {
      setValue("");
    }
  }, [externalValue]);

  const getEditorText = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const handleModelChange = (newModel) => {
    setValue(newModel);
    if (onChange) onChange(newModel);
  };

  // Helper to replace non-breaking spaces with regular spaces
  const normalizeSpaces = (text) => text.replace(/&nbsp;|\u202F|\u00A0/g, " ");

  // Trim only leading/trailing whitespace (including &nbsp;) **without** removing HTML tags like <a>
  const trimHtmlSpaces = (html) => {
    if (!html) return html;
    // Remove spaces immediately inside tags boundaries and at start/end
    let cleaned = html;
    cleaned = cleaned.replace(/^((?:\s|&nbsp;|\u00A0)+)/g, ""); // leading
    cleaned = cleaned.replace(/((?:\s|&nbsp;|\u00A0)+)$/g, ""); // trailing
    cleaned = cleaned.replace(/>(?:\s|&nbsp;|\u00A0)+/g, ">"); // after '>'
    cleaned = cleaned.replace(/(?:\s|&nbsp;|\u00A0)+</g, "<"); // before '<'
    return cleaned;
  };

  // Handle outside click for emoji picker
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target) &&
        emojiButtonRef.current &&
        !emojiButtonRef.current.contains(event.target)
      ) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showEmojiPicker]);

  // Handle emoji selection
  const handleEmojiSelect = (emojiObject) => {
    // Use the editor that actually opened the emoji picker
    const targetEditor = activeEditorRef.current || editorRef.current;

    if (targetEditor) {
      // Build emoji HTML like Froala native
      const emojiHTML = `<span class="fr-emoticon fr-deletable" data-emoji-styled="true">${emojiObject.emoji}</span>`;

      // Insert inline at restored caret position
      targetEditor.html.insert(emojiHTML, false);

      // Ensure font-inheritance attributes are applied
      preserveFontAfterEmoji(targetEditor);

      // Get the updated content and trigger onChange only
      const newContent = targetEditor.html.get();
      if (onChange) {
        onChange(newContent);
      }

      // Update character count ref immediately
      updateCharCountRef(targetEditor);

      // Trigger contentChanged event to ensure all handlers are called
      setTimeout(() => {
        targetEditor.events.trigger("contentChanged");
      }, 0);

      setShowEmojiPicker(false);

      // Clear the active editor reference
      activeEditorRef.current = null;
    }
  };

  useEffect(() => {
    if (FroalaEditorCore) {
      handleEditorCustomization(FroalaEditorCore);

      // Register custom emoji picker button with modern styling
      FroalaEditorCore.DefineIcon("customEmojis", {
        template: "text",
        NAME: "😊", // Use emoji as icon for now - we'll style it with CSS
      });

      // Add custom CSS styling for the emoji button and popup positioning
      const style = document.createElement("style");
      style.textContent = `
        .fr-command[data-cmd="emoticons"] {
          font-size: 16px !important;
          font-family: 'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif !important;
        }
        .fr-command[data-cmd="emoticons"]:hover {
          transform: scale(1.1);
          transition: transform 0.2s ease;
        }
      `;
      if (!document.querySelector("style[data-froala-emoji]")) {
        style.setAttribute("data-froala-emoji", "true");
        document.head.appendChild(style);
      }

      FroalaEditorCore.RegisterCommand("emoticons", {
        title: "Insert Emoji",
        icon: "customEmojis",
        undo: false,
        focus: false,
        callback: function () {
          // Save the current selection before opening the picker
          savedSelectionRef.current = this.selection.save();

          // Store reference to the editor that opened   the picker
          activeEditorRef.current = this;

          // Toggle emoji picker visibility
          setShowEmojiPicker((prev) => !prev);

          // Store reference to the button for positioning
          const toolbar = this.$tb[0];
          const emojiButton = toolbar.querySelector('[data-cmd="emoticons"]');
          if (emojiButton) {
            emojiButtonRef.current = emojiButton;
          }
        },
      });

      FroalaEditorCore.DefineIcon("customFontSize", { SVG_KEY: "fontSize" });
      FroalaEditorCore.RegisterCommand("customFontSize", {
        title: "Custom Font Size",
        icon: "customFontSize",
        undo: true,
        focus: true,
        callback: function () {
          const fontSize = prompt("Enter font size (in px):", "16");
          if (fontSize && !isNaN(fontSize)) {
            this.format.applyStyle("font-size", `${fontSize}px`);
          }
        },
      });

      // Register simple button for applying default bullets
      FroalaEditorCore.DefineIcon("simpleUL", { SVG_KEY: "unorderedList" });
      FroalaEditorCore.RegisterCommand("simpleUL", {
        title: "Bullets",
        icon: "simpleUL",
        undo: true,
        focus: true,
        refreshAfterCallback: true,
        callback: function () {
          // Apply default bullets (disc)
          let ulElement = this.selection.element();
          let isAlreadyList = false;

          // Find closest UL element from current selection
          while (ulElement && ulElement.tagName !== "UL") {
            if (ulElement.tagName === "LI") {
              isAlreadyList = true;
            }
            ulElement = ulElement.parentNode;
          }

          if (isAlreadyList) {
            // If already a list, remove it (toggle behavior)
            this.commands.exec("formatUL");
          } else {
            // If not a list, create one with default bullet style
            this.commands.exec("formatUL");

            // Find the newly created UL and apply disc style
            ulElement = this.selection.element();
            while (ulElement && ulElement.tagName !== "UL") {
              ulElement = ulElement.parentNode;
            }

            if (ulElement && ulElement.tagName === "UL") {
              ulElement.style.listStyleType = "disc";
            }
          }

          // Force content refresh
          this.events.trigger("contentChanged");
        },
      });

      // Register dropdown for bullet styles
      FroalaEditorCore.DefineIcon("bulletStyles", { SVG_KEY: "moreText" });
      FroalaEditorCore.RegisterCommand("bulletStyles", {
        title: "Bullet Styles",
        type: "dropdown",
        focus: false,
        undo: true,
        refreshAfterCallback: true,
        options: {
          bullet: "Bullet",
          circle: "Circle",
          square: "Square",
        },
        callback: function (cmd, val) {
          // Only process if a specific option was selected
          if (val) {
            let element = this.selection.element();
            let isAlreadyUL = false;
            let isAlreadyOL = false;
            let listElement = null;

            // Check if we're already in a list (UL or OL)
            while (
              element &&
              element.tagName !== "UL" &&
              element.tagName !== "OL"
            ) {
              if (element.tagName === "LI") {
                // Found a list item, now find its parent list
                let parent = element.parentNode;
                if (parent && parent.tagName === "UL") {
                  isAlreadyUL = true;
                  listElement = parent;
                } else if (parent && parent.tagName === "OL") {
                  isAlreadyOL = true;
                  listElement = parent;
                }
                break;
              }
              element = element.parentNode;
            }

            // If we found a UL or OL directly
            if (
              element &&
              (element.tagName === "UL" || element.tagName === "OL")
            ) {
              listElement = element;
              isAlreadyUL = element.tagName === "UL";
              isAlreadyOL = element.tagName === "OL";
            }

            // If we're in an ordered list, remove it first
            if (isAlreadyOL) {
              this.commands.exec("formatOL");
            }

            // If we're not in an unordered list, create one
            if (!isAlreadyUL) {
              this.commands.exec("formatUL");
            }

            // Apply selected style to the UL
            element = this.selection.element();
            while (element && element.tagName !== "UL") {
              element = element.parentNode;
            }

            if (element && element.tagName === "UL") {
              switch (val) {
                case "bullet":
                  element.style.listStyleType = "disc";
                  break;
                case "circle":
                  element.style.listStyleType = "circle";
                  break;
                case "square":
                  element.style.listStyleType = "square";
                  break;
              }
            }

            // Force content refresh
            this.events.trigger("contentChanged");
          }
        },
      });

      FroalaEditorCore.DefineIcon("textColorCustom", { SVG_KEY: "textColor" });
      FroalaEditorCore.RegisterCommand("textColorCustom", {
        title: "Overwrite colors for specific words or text",
        icon: "textColorCustom",
        undo: true,
        focus: true,
        callback: function () {
          this.commands.exec("textColor");
        },
        refreshAfterCallback: true,
      });

      FroalaEditorCore.DefineIcon("customBackgroundColor", {
        SVG_KEY: "backgroundColor",
      });
      FroalaEditorCore.RegisterCommand("customBackgroundColor", {
        title: "Highlight",
        icon: "customBackgroundColor",
        undo: true,
        focus: true,
        callback: function () {
          this.commands.exec("backgroundColor");
        },
        refreshAfterCallback: true,
      });

      // Register empty toolbar button that does nothing
      FroalaEditorCore.DefineIcon("emptyToolbar", { SVG_KEY: null });
      FroalaEditorCore.RegisterCommand("emptyToolbar", {
        title: "",
        icon: "emptyToolbar",
        undo: false,
        focus: false,
        callback: function () {},
      });
    }
  }, [handleEditorCustomization, editorRef]);

  const handleDrop = (dropEvent, editor) => {
    dropEvent.preventDefault();
    dropEvent.stopPropagation();

    if (
      dropEvent.originalEvent.dataTransfer.items &&
      dropEvent.originalEvent.dataTransfer.items[0].kind === "file"
    ) {
      return false;
    }
  };

  const handlePaste = (pasteEvent) => {
    const clipboardData = pasteEvent.clipboardData || window.clipboardData;
    if (clipboardData) {
      const items = clipboardData.items;
      if (items) {
        for (var i = 0; i < items.length; i++) {
          if (items[i].type.match("^image/") || items[i].kind === "file") {
            pasteEvent.preventDefault();
            return false;
          }
        }
      }

      const text = clipboardData.getData("text/plain");

      if (text && characters) {
        pasteEvent.preventDefault();
        if (editorRef.current) {
          const currentHtml = editorRef.current.html.get();
          // Count DECODED characters (entities like &nbsp; are 1 char, not 6)
          // so enforcement always matches the visible counter.
          const textOnlyLength = getCharacterCount(currentHtml);
          const selLength = window.getSelection().toString().length;
          const maxLength = characters;
          // account for selected text being overwritten
          const remaining = maxLength - (textOnlyLength - selLength);
          if (remaining <= 0) {
            return false;
          }
          const trimmedText = text.slice(0, remaining);
          editorRef.current.html.insert(trimmedText, true);
          const newContent = editorRef.current.html.get();

          // Force update state/UI
          if (onChange) {
            setTimeout(() => {
              onChange(newContent);
            }, 0);
          }
        }
        return false;
      }
    }
    return true; // Continue with other types of pasting if not handled above
  };

  // Smart paste handler for segment character limit
  const handleSmartSegmentPaste = (pasteEvent) => {
    const clipboardData = pasteEvent.clipboardData || window.clipboardData;
    if (!clipboardData) return true;

    // Block image/file pasting
    const items = clipboardData.items;
    if (items) {
      for (var i = 0; i < items.length; i++) {
        if (items[i].type.match("^image/") || items[i].kind === "file") {
          pasteEvent.preventDefault();
          return false;
        }
      }
    }

    const text = clipboardData.getData("text/plain");
    if (!text) return true;

    // Get real-time character count instead of relying on refs
    let currentSegmentChars;
    if (editorRef.current) {
      // Calculate the real current count by getting fresh content from the editor
      const currentContent = editorRef.current.html.get();
      const currentEditorCharCount = getCharacterCount(currentContent);
      // Calculate other editors' contribution by subtracting our initial contribution and adding current
      const otherEditorsCount =
        selectedSegmentCharCount - currentEditorContribution;
      currentSegmentChars = otherEditorsCount + currentEditorCharCount;
    } else {
      // Fallback to ref value if editor not available
      currentSegmentChars = selectedSegmentCharCountRef.current;
    }

    const remainingChars = charLimitInASegment - currentSegmentChars;

    if (remainingChars <= 0) {
      // No space left, block the paste
      toast.error(
        `Segment text limit (${charLimitInASegment} characters) has been reached.`
      );
      pasteEvent.preventDefault();
      return false;
    }

    if (text.length <= remainingChars) {
      // The entire text fits, allow normal paste
      return true;
    }

    // Text is too long, trim it to fit
    pasteEvent.preventDefault();

    if (editorRef.current) {
      const trimmedText = text.slice(0, remainingChars);
      editorRef.current.html.insert(trimmedText, true);

      // Show notification about trimmed paste
      toast.info(
        `Text was trimmed to fit within the ${charLimitInASegment} character limit. ${
          text.length - remainingChars
        } characters were removed.`
      );

      // Force update state/UI
      if (onChange) {
        setTimeout(() => {
          const newContent = editorRef.current.html.get();
          onChange(newContent);
        }, 0);
      }
    }

    return false;
  };

  let editorClass = "froalaEditor";
  if (type === "title") {
    editorClass += " froalaEditor--variantTitle";
  } else if (type === "description") {
    editorClass += " froalaEditor--variantDescription";
  }

  if (singleElement) {
    editorClass += " froalaEditor--singleElement";
  }
  if (isInteracting) {
    editorClass += " froalaEditor--active";
  }
  if (popupPositionMode === "relative") {
    editorClass += " froalaEditor--relativePopup";
  }

  const applyLinkStyles = (editor) => {
    const listItems = editor.el.querySelectorAll("li");
    listItems.forEach((li) => {
      // Select all 'a' elements within each 'li'
      const links = li.querySelectorAll("a");
      if (links.length) {
        // Directly check the inline style for color on the 'li'
        const liColor = li.style.color; // Gets the inline style color value
        links.forEach((link) => {
          if (liColor) {
            // If there's an inline color set, use it for each link
            console.log(liColor, "Applying color to link");
            link.style.color = liColor;
          } else {
            link.style.color = "#0000cc"; // Default blue if no inline color is set
          }
        });
      }
    });
  };

  // Updated preserveFontAfterEmoji function

  const preserveFontAfterEmoji = (editor) => {
    if (!editor) return;

    const frView = editor.el;
    if (!frView) return;

    // Apply the emoji font inheritance CSS property
    frView.style.setProperty(
      "--emoji-font-family",
      paragraphProps?.fontFamily || "'DM Sans', sans-serif"
    );

    // Find all emojis
    const emojis = frView.querySelectorAll(".fr-emoticon");

    // Set a data attribute on emojis to identify them for CSS targeting
    emojis.forEach((emoji) => {
      emoji.setAttribute("data-emoji-styled", "true");

      // Determine the parent element type
      const paragraphParent = emoji.closest("p");
      const headingParent = emoji.closest("h1, h2");

      if (paragraphParent) {
        emoji.setAttribute("data-parent-type", "paragraph");
      } else if (headingParent) {
        emoji.setAttribute("data-parent-type", "heading");
      }
    });
  };

  useEffect(() => {
    if (focused && editorRef.current) {
      editorRef.current?.events?.focus();
    }
  }, [focused]);

  useEffect(() => {
    if (editorRef.current && lineHeight) {
      const frView = editorRef.current.el;
      if (frView) {
        frView.style.setProperty("--line-height", lineHeight);
      }

      setTimeout(() => {
        editorRef.current.events.trigger("contentChanged");
      }, 0);
    }
  }, [lineHeight]);

  useEffect(() => {
    if (editorRef.current && headingProps) {
      applyHeadingProps(editorRef.current, headingProps);

      // Also call our custom function to handle emojis
      preserveFontAfterEmoji(editorRef.current);

      setTimeout(() => {
        editorRef.current.events.trigger("contentChanged");
      }, 0);
    }
  }, [headingProps]);

  useEffect(() => {
    if (editorRef.current && headingProps?.resetKey > 0) {
      removeInlineHeadingColors(editorRef.current);
    }
  }, [headingProps?.resetKey]);

  useEffect(() => {
    if (editorRef.current && paragraphProps) {
      applyParagraphProps(editorRef.current, paragraphProps);

      // Also call our custom function to handle emojis
      preserveFontAfterEmoji(editorRef.current);

      setTimeout(() => {
        editorRef.current.events.trigger("contentChanged");
      }, 0);
    }
  }, [paragraphProps]);

  useEffect(() => {
    if (editorRef.current && paragraphProps?.resetKey > 0) {
      removeInlineParagraphColors(editorRef.current);
    }
  }, [paragraphProps?.resetKey]);

  return (
    <>
      {label ? (
        <div
          className={`editorLabel ${
            isInteracting ? "editorLabel--active" : ""
          }`}
        >
          {label}
        </div>
      ) : dynamicLabelName ? (
        <ContentEditable
          html={transformEditorText(dynamicLabel)}
          disabled
          className={`editorLabel ${
            isInteracting ? "editorLabel--active" : ""
          }`}
          // style={{ marginBottom: "0px" }}
        />
      ) : null}

      <div className={editorClass} id={`froala-editor-${id}`}>
        <FroalaEditor
          tag="textarea"
          config={{
            key: "ZOD3gB7B10A5D6B2G2B2C-7SLJCKHXOSLMc2YGSGe1ZXHSa1CgC3E3G3H3B7A4E3F4B2C1==",
            // Replace fixed height with min-height using relative units
            autofocus,

            height: isExpandable
              ? "auto"
              : editorHeight
              ? editorHeight
              : "15rem",
            heightMin: isExpandable ? "4rem" : null,
            heightMax: isExpandable ? "18rem" : null,

            // Make sure the editor is responsive
            width: "100%",

            // Make toolbar responsive
            toolbarSticky: false,
            toolbarStickyOffset: 0,

            // Adjust based on container width
            zIndex: 2,
            iframe: false,

            // Smaller buttons on smaller screens
            toolbarButtonsXS:
              toolbarButtons.length === 0
                ? ["emptyToolbar"]
                : toolbarButtons.length > 5
                ? toolbarButtons.slice(0, 5).concat(["moreText"])
                : toolbarButtons,

            // Keep the rest of your config the same
            placeholderText: placeholder,

            charCounterCount: false,
            charLimit: characters,

            tabSpaces: 4,

            // toolbarContainer: "#customToolbarFroala",
            linkAlwaysBlank: true,
            linkInsertButtons: [],
            fontFamilySelection: true,
            fontSizeSelection: true,
            // fontFamilyDefaultSelection: "DM Sans",
            // fontSizeDefaultSelection: customDefaultFontSize || "16",
            colorsHEXInput: true,
            fontSize: [
              "8",
              "9",
              "10",
              "12",
              "14",
              "16",
              "18",
              "20",
              "24",
              "30",
              "36",
              "48",
              "60",
              "72",
              "96",
            ],
            attribution: false,
            paragraphFormatSelection: true,
            paragraphDefaultSelection: "Paragraph",
            keepFormatOnDelete: true,
            pasteDeniedAttrs: ["id", "class", "style"],
            pastePlain: true,
            pasteAllowedStyleProps: [],
            pasteAllowLocalImages: false,
            lineHeights: {
              Single: "1",
              1.15: "1.15",
              1.25: "1.25",
              1.5: "1.5",
              Double: "2",
            },
            paragraphFormat: {
              N: "Paragraph",
              H1: "Heading",
            },

            fontFamily: {
              "DM Sans, sans-serif": "DM Sans",
              "Arial,Helvetica,sans-serif": "Arial",
              "Georgia,serif": "Georgia",
              "Impact,Charcoal,sans-serif": "Impact",
              "Tahoma,Geneva,sans-serif": "Tahoma",
              "'Times New Roman',Times,serif": "Times New Roman",
              "Verdana,Geneva,sans-serif": "Verdana",
              "'Poppins', sans-serif": "Poppins",
              "'Quando', serif": "Quando",
            },

            toolbarButtons:
              toolbarButtons.length === 0 ? ["emptyToolbar"] : toolbarButtons,

            pluginsEnabled: [
              "align",
              "charCounter",
              "codeBeautifier",
              "colors",
              "draggable",
              // "emoticons", // Disabled default emoticons plugin
              "fontSize",
              "lineHeight",
              "link",
              "lists",
              "paragraphFormat",
              "paragraphStyle",
              "quote",
              "save",
              "url",
              "fontFamily",
              "specialCharacters",
            ],

            // Disable default emoticons since we're using custom emoji picker
            // emoticonsUseImage: false,
            imageUpload: false,
            fileUpload: false,
            imagePaste: false,

            events: {
              initialized: function () {
                editorRef.current = this;
                if (focused && editorRef.current.events) {
                  editorRef.current.events.focus();
                }

                // Set data attribute for editor identification
                if (dataEditorId && this.el) {
                  this.el.setAttribute("data-editor-id", dataEditorId);
                }

                const resizeObserver = new ResizeObserver(() => {
                  this.size.refresh();
                });

                // Observe the editor container
                if (this.el && this.el.parentNode) {
                  resizeObserver.observe(this.el.parentNode);
                }
                const editor = this;

                if (headingProps) {
                  applyHeadingProps(this, headingProps);
                }
                if (paragraphProps) {
                  applyParagraphProps(this, paragraphProps);
                }

                if (lineHeight) {
                  const frView = this.el;
                  if (frView) {
                    frView.style.setProperty("--line-height", lineHeight);
                  }
                }

                // Initialize character count ref
                updateCharCountRef(this);

                editor.events.on(
                  "drop",
                  function (dropEvent) {
                    dropEvent.preventDefault(); // Prevents the default drop behavior
                    dropEvent.stopPropagation(); // Stops the event from propagating further
                    return false; // Tells Froala to do nothing further with this event
                  },
                  true
                );
              },
              focus: function () {
                setIsInteracting(true);
              },
              blur: function () {
                setIsInteracting(false);

                // Trim URLs immediately when user finishes editing (on blur)
                if (trimUrls) {
                  const currentContent = this.html.get();
                  const cleanedHtml = trimHtmlSpaces(currentContent);
                  const cleanText = normalizeSpaces(cleanedHtml);

                  // Only update if trimming actually changed something
                  if (cleanedHtml !== currentContent) {
                    this.html.set(cleanText);
                    setValue(cleanText);
                    if (onChange) onChange(cleanText);
                    this.selection.setAtEnd(this.$el.get(0));
                  }
                }

                if (onBlur) onBlur();
              },
              "commands.after": function (cmd, param1, param2) {
                setIsInteracting(true);

                // Check if the command was to insert an emoji
                // if (cmd === "emoticons") {
                //   // Apply proper font family to emojis after insertion
                //   preserveFontAfterEmoji(this);

                //   // Immediately update the content
                //   const newContent = this.html.get();
                //   setValue(newContent);
                //   if (onChange) {
                //     onChange(newContent);
                //   }

                //   // Trigger content changed to refresh the editor
                //   setTimeout(() => {
                //     this.events.trigger("contentChanged");
                //   }, 10);
                // }
              },

              // Add emoji-specific event handling
              "emoticons.inserted": function () {
                // Apply font preservation immediately after emoji insertion
                preserveFontAfterEmoji(this);
              },

              contentChanged: function () {
                if (isExpandable) {
                  // Automatically adjust height based on content
                  this.opts.height = "auto";
                  this.size.refresh();
                }

                // Apply link styles
                applyLinkStyles(this);

                // Apply font preservation to any emojis
                preserveFontAfterEmoji(this);

                // Update character count ref for segment limit checking
                updateCharCountRef(this);
              },
              input: function () {
                // Update character count ref on any input
                updateCharCountRef(this);
              },
              keyup: function () {
                // Update character count ref after any key release (handles deletions)
                updateCharCountRef(this);
              },
              drop: handleDrop,
              paste: function (pasteEvent) {
                // Use smart paste handler if segment character limit is enabled
                if (disableAtCharLimit) {
                  return handleSmartSegmentPaste(pasteEvent);
                }

                // Continue with the existing paste handler for individual editor limits
                return handlePaste(pasteEvent);
              },
              "paste.before": function (pasteEvent) {
                // Use smart paste handler if segment character limit is enabled
                if (disableAtCharLimit) {
                  return handleSmartSegmentPaste(pasteEvent);
                }

                // Continue with the existing paste handler for individual editor limits
                return handlePaste(pasteEvent);
              },

              keydown: function (e) {
                // Real-time character limit check if segment limit is enabled
                if (disableAtCharLimit) {
                  // Allow navigation keys, selection keys, and keyboard shortcuts
                  const allowedKeys = [
                    "ArrowUp",
                    "ArrowDown",
                    "ArrowLeft",
                    "ArrowRight",
                    "Home",
                    "End",
                    "PageUp",
                    "PageDown",
                    "Tab",
                    "Escape",
                    "F1",
                    "F2",
                    "F3",
                    "F4",
                    "F5",
                    "F6",
                    "F7",
                    "F8",
                    "F9",
                    "F10",
                    "F11",
                    "F12",
                  ];

                  // Allow selection and deletion operations
                  const isSelectionKey =
                    e.shiftKey &&
                    [
                      "ArrowUp",
                      "ArrowDown",
                      "ArrowLeft",
                      "ArrowRight",
                      "Home",
                      "End",
                      "PageUp",
                      "PageDown",
                    ].includes(e.key);
                  const isDeletionKey = ["Backspace", "Delete"].includes(e.key);
                  const isShortcutKey = e.ctrlKey || e.metaKey;

                  // Check if this is a character that would add content
                  const isCharacterKey =
                    e.key.length === 1 && !isShortcutKey && !e.altKey;

                  if (isCharacterKey) {
                    // Get current content from the editor
                    const currentContent = this.html.get();
                    const currentCharCount = getCharacterCount(currentContent);

                    // Get the selected text length (in case of replacement)
                    const selectedTextLength = this.selection.text().length;

                    // Calculate other editors' contribution
                    // Use the initial contribution and current count to get accurate total
                    const otherEditorsCount =
                      selectedSegmentCharCount - currentEditorContribution;

                    // Calculate what the new total would be after this character
                    // Account for selected text that will be replaced
                    const projectedTotal =
                      otherEditorsCount +
                      currentCharCount +
                      1 -
                      selectedTextLength;

                    // Check if adding this character would exceed the limit
                    if (projectedTotal > charLimitInASegment) {
                      // Show toast notification when trying to type at limit
                      toast.error(
                        `Segment text limit (${charLimitInASegment} characters) has been reached.`
                      );
                      e.preventDefault();
                      return false;
                    }
                  }

                  // Block typing new characters when at limit unless it's allowed
                  if (
                    isAtCharLimitRef.current &&
                    !allowedKeys.includes(e.key) &&
                    !isSelectionKey &&
                    !isDeletionKey &&
                    !isShortcutKey
                  ) {
                    // Show toast notification when trying to type at limit
                    toast.error(
                      `Segment text limit (${charLimitInASegment} characters) has been reached.`
                    );
                    e.preventDefault();
                    return false;
                  }
                }

                // Existing character limit code for individual editors
                const editor = this;
                const max = editor.opts.charLimit;
                if (!max) return;
                // Count DECODED characters (entities like &nbsp; are 1 char,
                // not 6) so enforcement always matches the visible counter.
                const plainLength = getCharacterCount(editor.html.get());
                const sel = window.getSelection().toString();
                if (
                  e.key.length === 1 &&
                  !e.ctrlKey &&
                  !e.metaKey &&
                  !e.altKey
                ) {
                  const projected = plainLength - sel.length + 1;
                  if (projected > max) {
                    e.preventDefault();
                    return false;
                  }
                }
                // only printable keys
              },
            },
          }}
          model={value}
          onModelChange={handleModelChange}
          disabled={disabled}
          data-editor-id={dataEditorId}
        />
        {characters && (
          <div
            className="character-counter"
            style={{
              textAlign: "right",
              fontSize: "12px",
              color: getCharacterCount(value) > characters ? "#ff4d4f" : "#999",
              margin: "5px 0 0px 0",
            }}
          >
            {getCharacterCount(value) > characters
              ? `${characters}/${characters}`
              : `${getCharacterCount(value)}/${characters}`}
          </div>
        )}

        {/* Custom Emoji Picker */}
        {showEmojiPicker && (
          <>
            {/* Custom CSS to fix skin tone picker overlap */}
            <style>
              {`
                .epr_q53mwh.epr_-8ygbw8.epr_346qu6.epr_-lzq3oa {
                  padding-bottom: 0 !important;
                }
                .epr-search-container {
                  position: relative !important;
                }
                .epr-icn-search {
                  position: absolute !important;
                  right: 12px !important;
                  top: 28% !important;
                  transform: translateY(-50%) !important;
                  pointer-events: none !important;
                  color: #666 !important;
                }
                .epr-skin-tones {
                  position: relative !important;
                  flex-basis: 0px !important;
                  margin-top: 38px !important;
                }
                .epr-skin-tone-select {
                  position: relative !important;
                  left: auto !important;
                  bottom: auto !important;
                  right: auto !important;
                  top: auto !important;
                }
              `}
            </style>
            <Box
              ref={emojiPickerRef}
              style={{
                position: "fixed",
                zIndex: 9999,
                top: "50vh",
                left: "50%",
                transform: "translate(-50%, -50%)",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                borderRadius: "8px",
                overflow: "visible",
                backgroundColor: "white",
                border: "1px solid #e0e0e0",
                padding: "0",
              }}
            >
              <EmojiPicker
                onEmojiClick={handleEmojiSelect}
                theme="light"
                autoFocusSearch={true}
                searchPlaceholder="Search emojis..."
                width={350}
                height={400}
                skinTonesDisabled={false}
                previewConfig={{
                  showPreview: true,
                  defaultEmoji: "1f60a",
                  defaultCaption: "Choose your emoji!",
                }}
                searchDisabled={false}
              />
              <Box
                onClick={() => setShowEmojiPicker(false)}
                style={{
                  borderRadius: "50%",
                  cursor: "pointer",
                  position: "absolute",
                  right: 10,
                  top: 10,
                  height: "24px",
                  width: "24px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "rgba(240, 240, 240, 0.95)",
                  zIndex: 10000,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  border: "1px solid #e0e0e0",
                }}
              >
                <Close
                  style={{
                    fontSize: "18px",
                    color: "#555",
                  }}
                />
              </Box>
            </Box>
          </>
        )}
      </div>
    </>
  );
};

export default FroalaTextEditor;
